import { useMemo, useRef, useState } from "react";
import type { MouseEvent } from "react";
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  IconButton,
  ListItem,
  Text,
  UnorderedList,
  useDisclosure,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { Cohort } from "../../../api/schemas/cohort";
import { deleteCohort, removePatientsFromCohort } from "../../../api/internal";
import useMetriportToast from "../../shared/toast";
import { captureAndDisplayError } from "../../shared/util";

const monitoringLabels: Record<string, string> = {
  "adt.enabled": "ADT alerts",
  "hie.enabled": "HIE pulls",
  "pharmacy.notifications": "Pharmacy real-time notifications",
  "pharmacy.schedule.enabled": "Pharmacy scheduled history pull",
  "laboratory.notifications": "Lab result notifications",
};

type DeletePhase = "idle" | "removing-patients" | "deleting";

function getEnabledMonitoringFlags(value: unknown, path: string[] = []): string[] {
  if (!value || typeof value !== "object") return [];

  return Object.entries(value).flatMap(([key, childValue]) => {
    const currentPath = [...path, key];
    const pathKey = currentPath.join(".");

    if (typeof childValue === "boolean") {
      if (!childValue) return [];
      return [monitoringLabels[pathKey] ?? pathKey];
    }

    return getEnabledMonitoringFlags(childValue, currentPath);
  });
}

export default function CohortDeleteButton({
  cxId,
  cohort,
  onDelete,
  variant = "button",
}: {
  cxId: string;
  cohort: Cohort;
  onDelete?: (cohortId: string) => void;
  variant?: "button" | "icon";
}) {
  const [deletePhase, setDeletePhase] = useState<DeletePhase>("idle");
  const deleteCancelRef = useRef<HTMLButtonElement>(null);
  const toast = useMetriportToast();

  const enabledMonitoringFlags = useMemo(() => {
    return getEnabledMonitoringFlags(cohort.settings.monitoring);
  }, [cohort.settings.monitoring]);

  const patientCount = cohort.size ?? 0;
  const isSubmitting = deletePhase !== "idle";
  const hasDeleteWarnings = patientCount > 0 || enabledMonitoringFlags.length > 0;
  const deleteButtonText =
    deletePhase === "removing-patients" ? "Removing patients..." : "Deleting...";

  const {
    isOpen: isDeleteConfirmOpen,
    onOpen: onOpenDeleteConfirm,
    onClose: onCloseDeleteConfirm,
  } = useDisclosure();

  function openDeleteConfirm(event?: MouseEvent) {
    event?.stopPropagation();
    onOpenDeleteConfirm();
  }

  function closeDeleteConfirm() {
    if (isSubmitting) return;
    onCloseDeleteConfirm();
  }

  async function handleDeleteCohort() {
    try {
      if (patientCount > 0) {
        setDeletePhase("removing-patients");

        await removePatientsFromCohort({
          cxId,
          cohortId: cohort.id,
          body: JSON.stringify({ allPatients: true }),
        });
      }

      setDeletePhase("deleting");

      await deleteCohort(cxId, cohort.id);

      toast.success({ title: "Cohort deleted." });
      onDelete?.(cohort.id);
      onCloseDeleteConfirm();
    } catch (error) {
      captureAndDisplayError({
        error,
        msg: "Failed to delete cohort",
        context: "delete",
        captureMsg: "Component: CohortDeleteButton - delete failed",
        toast,
        extras: { cxId, cohortId: cohort.id },
      });
    } finally {
      setDeletePhase("idle");
    }
  }

  return (
    <>
      {variant === "icon" ? (
        <IconButton
          aria-label="Delete cohort"
          icon={<DeleteIcon />}
          colorScheme="red"
          variant="ghost"
          size="sm"
          onClick={openDeleteConfirm}
          isDisabled={isSubmitting}
        />
      ) : (
        <Button
          id="delete-cohort"
          colorScheme="red"
          size="sm"
          mt={3}
          onClick={openDeleteConfirm}
          isDisabled={isSubmitting}
        >
          Delete Cohort
        </Button>
      )}

      <AlertDialog
        isOpen={isDeleteConfirmOpen}
        leastDestructiveRef={deleteCancelRef}
        onClose={closeDeleteConfirm}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete cohort?
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text>
                Are you sure you want to delete <strong>{cohort.name}</strong>? This action cannot
                be undone.
              </Text>

              {hasDeleteWarnings && (
                <Alert status="warning" mt={4} alignItems="flex-start">
                  <AlertIcon mt={1} />
                  <Box>
                    <AlertTitle>This cohort is still in use.</AlertTitle>
                    <AlertDescription>
                      <Text mt={1}>Review the following before deleting this cohort:</Text>

                      <UnorderedList mt={2} spacing={1}>
                        {patientCount > 0 && (
                          <ListItem>
                            {patientCount} patient
                            {patientCount === 1 ? "" : "s"} assigned to this cohort.
                          </ListItem>
                        )}

                        {enabledMonitoringFlags.map(flag => (
                          <ListItem key={flag}>{flag} is enabled</ListItem>
                        ))}
                      </UnorderedList>
                    </AlertDescription>
                  </Box>
                </Alert>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={deleteCancelRef} onClick={closeDeleteConfirm} isDisabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteCohort}
                isLoading={isSubmitting}
                loadingText={deleteButtonText}
                ml={3}
              >
                Delete Cohort
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
